import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getAuthHeader } from "../utils/auth"; // ★ 1. 認証ヘッダー取得関数をインポート
import { useOutletContext } from "react-router-dom";

// --- SVG Icons (Unused icons removed) ---
const UploadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>;

// --- Components ---
function EditableCell({ value, onSave, style = {} }) {
  const [editing, setEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value || "");
  useEffect(() => {
    setCurrentValue(value || "");
  }, [value]);
  const handleSave = () => {
    setEditing(false);
    if (currentValue !== value) {
      onSave(currentValue);
    }
  };
  return (
    <td className="py-3 px-4" onDoubleClick={() => setEditing(true)} style={style}>
      {editing ? (
        <input
          type="text"
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
          }}
          className="w-full p-1 border rounded-md"
          autoFocus
        />
      ) : (
        <div className="truncate" title={value}>{value}</div>
      )}
    </td>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
    if (totalPages <= 1) {
        return null;
    }
    return (
        <div className="flex items-center justify-center mt-4">
            <button onClick={() => onPageChange(1)} disabled={currentPage === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">最初</button>
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">前へ</button>
            <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">次へ</button>
            <button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">最後</button>
        </div>
    );
}

function SalesList() {
  const { user, setActiveJob, activeJob, clientId } = useOutletContext();

  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [csvFile, setCsvFile] = useState(null);
  const [filterText, setFilterText] = useState("");

  const API_URL = "https://salesbreaker.jp";

  const fetchList = useCallback((pageToLoad) => {
    const clientIdToFetch = clientId || 'all';
    
    const payload = {
        client_id: clientIdToFetch,
        page: pageToLoad,
        limit: 100,
        filters: { // 検索条件をfiltersオブジェクトにまとめる
            searchTerm: filterText,
        }
    };

    fetch(`${API_URL}/api/load`, {
        method: "POST",
        headers: { // ★ 2. 認証ヘッダーを追加
            "Content-Type": "application/json",
            ...getAuthHeader()
        },
        body: JSON.stringify(payload),
    })
      .then(res => {
          if (!res.ok) throw new Error('サーバーからの応答が不正です。');
          return res.json();
      })
      .then(result => {
        if(result.success) {
          setList(result.data || []);
          setPagination(result.pagination || null);
        } else {
          console.error("リストの読み込みに失敗:", result.error);
          alert(`リストの読み込みに失敗しました: ${result.error}`);
          setList([]);
          setPagination(null);
        }
      })
      .catch(err => {
          console.error("営業リストの取得に失敗:", err);
          alert("営業リストの取得中に通信エラーが発生しました。");
          setList([]);
          setPagination(null);
      });
  }, [clientId, filterText]);

  useEffect(() => {
    fetchList(currentPage);
  }, [currentPage, fetchList]);
  
  const handlePageChange = (newPage) => {
    if (newPage > 0 && pagination && newPage <= pagination.totalPages) {
        setCurrentPage(newPage);
    }
  };

  const handleSearch = () => {
      setCurrentPage(1);
      fetchList(1);
  };

  const handleUpload = async () => {
    if (!csvFile) return alert("CSVファイルを選択してください。");
    if (activeJob) return alert("現在、別のバックグラウンド処理が実行中です。");

    const formData = new FormData();
    formData.append("file", csvFile);
    formData.append("client_id", clientId || '');

    try {
      setActiveJob({ status: 'starting' });
      const res = await fetch(`${API_URL}/api/sales/upload-job`, {
        method: "POST",
        headers: getAuthHeader(), // ★ 2. 認証ヘッダーを追加
        body: formData });
      if (res.status !== 202) {
        const errData = await res.json();
        throw new Error(errData.error || 'サーバーエラー');
      }
      const result = await res.json();
      if (result.success && result.jobId) {
        setActiveJob({ id: result.jobId, status: 'pending' });
        alert("アップロード処理の受付が完了しました。進捗は画面右下をご確認ください。");
      } else {
        throw new Error(result.error || 'ジョブの開始に失敗しました。');
      }
    } catch (err) {
      alert(`エラー: ${err.message}`);
      setActiveJob(null);
    }
  };

  const handleEdit = async (id, key, value) => {
    try {
      const res = await fetch(`${API_URL}/api/update/${id}`, {
        method: "POST",
        headers: { // ★ 2. 認証ヘッダーを追加
            "Content-Type": "application/json",
            ...getAuthHeader()
        }, body: JSON.stringify({ key, value }),
      });
      const result = await res.json();
      if (result.success) {
        setList(prevList => prevList.map(item => item.id === id ? { ...item, [key]: value } : item));
      } else {
        alert("更新に失敗しました。");
        fetchList(currentPage);
      }
    } catch (err) {
      alert("更新に失敗しました。");
      fetchList(currentPage);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("この行を削除してもよろしいですか？")) return;
    try {
      await fetch(`${API_URL}/api/delete/${id}`, {
        method: "DELETE",
        headers: getAuthHeader() // ★ 2. 認証ヘッダーを追加
      });
      setList(prevList => prevList.filter(item => item.id !== id));
    } catch (err) {
      alert("削除に失敗しました。");
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">🗂️ 営業リスト管理</h1>
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">CSV操作</h2>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0])} className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 sm:w-auto"/>
              <button onClick={handleUpload} disabled={!!activeJob} className="flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
                <UploadIcon /> {activeJob ? '処理中...' : 'アップロード'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">CSVファイルをアップロードして、リストを追加またはマージします。</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-4 border-b flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <input type="text" placeholder="リスト全体を検索..." value={filterText} onChange={(e) => setFilterText(e.target.value)} className="w-full p-2 border rounded-md"/>
            <button onClick={handleSearch} className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700">検索</button>
          </div>
          <div className="divide-y divide-gray-100 md:hidden">
            {list.length === 0 ? (
              <div className="p-8 text-center text-gray-500">表示できるデータがありません。</div>
            ) : (
              list.map((item) => (
                <div key={`${item.id}-mobile`} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="break-words font-semibold text-gray-900">{item.company || '-'}</p>
                      <p className="mt-1 break-words text-xs text-gray-500">{item.industry_class || '-'}</p>
                    </div>
                    <button onClick={() => handleDelete(item.id)} className="shrink-0 rounded-full bg-red-50 p-2 text-red-500" title="削除">
                      <DeleteIcon />
                    </button>
                  </div>
                  <div className="mt-3 space-y-1 text-xs text-gray-600">
                    <p className="break-all"><span className="font-semibold text-gray-500">URL:</span> {item.url ? <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{item.url}</a> : '-'}</p>
                    <p className="break-all"><span className="font-semibold text-gray-500">問い合わせURL:</span> {item.contact_url ? <a href={item.contact_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{item.contact_url}</a> : '-'}</p>
                    <p><span className="font-semibold text-gray-500">住所:</span> {item.address || '-'}</p>
                    <p><span className="font-semibold text-gray-500">電話:</span> {item.phone || '-'}</p>
                    <p><span className="font-semibold text-gray-500">資本金:</span> {item.capital || '-'}</p>
                    <p><span className="font-semibold text-gray-500">従業員数:</span> {item.employee_count || '-'}</p>
                    <p><span className="font-semibold text-gray-500">設立年月日:</span> {item.establishment_date || '-'}</p>
                    <p><span className="font-semibold text-gray-500">代表者:</span> {item.representative_name || '-'}</p>
                    <p className="break-words"><span className="font-semibold text-gray-500">事業タグ:</span> {item.business_tags || '-'}</p>
                    <p className="break-words"><span className="font-semibold text-gray-500">リストタグ:</span> {item.list_tags || '-'}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm text-left text-gray-600">
              <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                {/* ★★★ 修正点1: テーブルヘッダーに新しいカラムを追加 ★★★ */}
                <tr>
                  <th className="py-3 px-4">会社名</th>
                  <th className="py-3 px-4">URL</th>
                  <th className="py-3 px-4">問い合わせ先URL</th>
                  <th className="py-3 px-4">業種</th>
                  <th className="py-3 px-4">郵便番号</th>
                  <th className="py-3 px-4">住所</th>
                  <th className="py-3 px-4">電話番号</th>
                  <th className="py-3 px-4">資本金</th>
                  <th className="py-3 px-4">従業員数</th>
                  <th className="py-3 px-4">設立年月日</th>
                  <th className="py-3 px-4">代表者名</th>
                  <th className="py-3 px-4">事業タグ</th>
                  <th className="py-3 px-4">リストタグ</th>
                  <th className="py-3 px-4">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {list.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    {/* ★★★ 修正点2: テーブルボディに新しいカラムのEditableCellを追加 ★★★ */}
                    <EditableCell value={item.company} onSave={(v) => handleEdit(item.id, 'company', v)} style={{fontWeight: '500', color: '#1f2937'}}/>
                    <EditableCell value={item.url} onSave={(v) => handleEdit(item.id, 'url', v)} />
                    <EditableCell value={item.contact_url} onSave={(v) => handleEdit(item.id, 'contact_url', v)} />
                    <EditableCell value={item.industry_class} onSave={(v) => handleEdit(item.id, 'industry_class', v)} />
                    <EditableCell value={item.postal_code} onSave={(v) => handleEdit(item.id, 'postal_code', v)} />
                    <EditableCell value={item.address} onSave={(v) => handleEdit(item.id, 'address', v)} />
                    <EditableCell value={item.phone} onSave={(v) => handleEdit(item.id, 'phone', v)} />
                    <EditableCell value={item.capital} onSave={(v) => handleEdit(item.id, 'capital', v)} />
                    <EditableCell value={item.employee_count} onSave={(v) => handleEdit(item.id, 'employee_count', v)} />
                    <EditableCell value={item.establishment_date} onSave={(v) => handleEdit(item.id, 'establishment_date', v)} />
                    <EditableCell value={item.representative_name} onSave={(v) => handleEdit(item.id, 'representative_name', v)} />
                    <EditableCell value={item.business_tags} onSave={(v) => handleEdit(item.id, 'business_tags', v)} />
                    <EditableCell value={item.list_tags} onSave={(v) => handleEdit(item.id, 'list_tags', v)} />
                    <td className="py-3 px-4">
                      <button onClick={() => handleDelete(item.id)} className="p-2 text-red-500 hover:bg-red-100 rounded-full" title="削除">
                        <DeleteIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            <div className="p-4 text-sm text-gray-500 border-t">
              {pagination ? `${pagination.totalItems.toLocaleString()} 件中` : 'データを読み込み中...'}
            </div>
            <Pagination currentPage={currentPage} totalPages={pagination ? pagination.totalPages : 0} onPageChange={handlePageChange} />
        </div>
      </div>
    </div>
  );
}

export default SalesList;
